/* eslint-disable no-param-reassign */
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import DynamicFormField from 'src/shared/components/form/dynamicFormField';
import { createFormMember, createFormOther, getNewFormMember, getNewFormOther } from 'src/shared/services/formMemberService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useNavigate } from 'react-router-dom';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { useGlobals } from 'src/shared/contexts/appContext';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { ArrowForwardIosSharp } from '@mui/icons-material';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { FormItemResponse, FormMemberResponse } from '../models/formMemberResponse';
import { FormMemberPost, FormMemberPostItem } from '../models/formMemberPost';
import { MemberServerErrorSummary } from './memberServerErrorSummary';
import { MemberAddProfileDialog } from './memberAddProfileDialog';
import { MemberCreateModeSelector } from './memberCreateModeSelector';
import { MemberAccordionWrapper } from './memberAccordionWrapper';

type MemberCreateModalProps = {
  otherContactMode: boolean;
  modalOpen: boolean;
  onSave: () => void;
  onClose: () => void;
};

interface ColumnOrder {
  cols: string[];
}

export const MemberCreateModal = (props: MemberCreateModalProps) => {
  const { otherContactMode, modalOpen, onSave, onClose } = props;
  const { currentUser } = useGlobals();
  const { t } = useTranslation(['member', 'common']);
  const [loading, setIsLoading] = useState(false);
  const [localAssId, setLocalAssId] = useState<number>();
  const [showAssociations, setShowAssociations] = useState<boolean>(false);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [fieldsReady, setFieldsReady] = useState<boolean>(false);
  const [payingMember, setPayingMember] = useState<boolean | undefined>(undefined);
  const [rowsAndColsOrder, setRowsAndColsOrder] = useState<ColumnOrder[]>([]);
  const [skipValidation, setSkipValidation] = useState<boolean>(false);
  const [showProfileDialog, setShowProfileDialog] = useState<boolean>(false);
  const [createdMemberId, setCreatedMemberId] = useState<string | undefined>(undefined);

  const otherLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabel, t('member:otherContact.pageHeader'));
  const displayLabel = otherContactMode ? otherLabel : t('member:editMember.memberLabel');

  // Make a reference to the top portion of the modal content
  // When an error occures, scroll top of modal into view
  const modalTop = useRef<HTMLDivElement>(null);
  const scrollToModalTop = () => modalTop.current && modalTop.current.scrollIntoView();

  const isMounted = useIsMounted();
  const navigate = useNavigate();

  const formMethods = useForm<FormMemberResponse>({
    defaultValues: {
      items: [{}],
    },
  });

  const { control, handleSubmit, reset } = formMethods;

  const { fields } = useFieldArray({
    control,
    name: 'items',
  });

  useEffect(() => {
    if (!modalOpen) {
      setPayingMember(undefined);
    }
  }, [modalOpen]);

  useEffect(() => {
    const fetchFormSettings = async () => {
      setIsLoading(true);
      try {
        const result = await (otherContactMode ? getNewFormOther(payingMember, localAssId!) : getNewFormMember(payingMember, localAssId));
        if (isMounted()) {
          // add default values for null
          result.data.items.map((item) => {
            const nullCheck = (val: any) => {
              if (val === null) {
                if (typeof item.formValue === 'number') {
                  return 0;
                }
                return '';
              }
              return val;
            };

            return Object.assign(item, { value: nullCheck(item.formValue) });
          });

          // Step 1: Group items by groupID
          const groupMap = new Map<number, FormItemResponse[]>();

          result.data.items
            .filter((object) => object.formKey !== null)
            .forEach((object) => {
              const { groupID } = object.formSetting;
              const groupItems = groupMap.get(groupID) || [];
              groupItems.push(object);
              groupMap.set(groupID, groupItems);
            });

          const sortedFormItems: FormItemResponse[] = [];

          // Step 2: Sort within each group by row and col
          groupMap.forEach((items) => {
            const uniqueRowIds = new Set<number>();

            // Find all unique rows within the current group
            items.forEach((item) => {
              uniqueRowIds.add(item.formSetting.row);
            });

            // Sort items within the group by row and column
            Array.from(uniqueRowIds)
              .sort((a, b) => a - b)
              .forEach((rowId) => {
                const colItems = items.filter((item) => item.formSetting.row === rowId).sort((a, b) => a.formSetting.col - b.formSetting.col);
                colItems.forEach((child) => {
                  // Lock some fields for both paying and non paying member on create
                  if (child.formKey === 'resDate' || child.formKey === 'resCause') {
                    child.formSetting.locked = true;
                    child.formValue = null;
                  }

                  // Lock some fields for non paying member
                  if (!payingMember && (child.formKey === 'admDate' || child.formKey === 'expireDate')) {
                    child.formSetting.locked = true;
                    child.formValue = null;
                  }

                  // Set default value to today when creating a new unpaid member
                  if (payingMember && child.formKey === 'admDate') {
                    child.formValue = DateHelper.getFSDate(new Date());
                  }

                  // empty datepicker as default
                  if (payingMember && child.formKey === 'expireDate') {
                    child.formValue = null;
                  }

                  // localAss_id is selected in a previous step and used to get some dependent settings from the API, therefore locked here
                  if (child.formKey === 'localAss_id') {
                    child.formSetting.locked = true;
                    child.formValue = localAssId ? `${localAssId}` : '0';
                    child.value = localAssId ? `${localAssId}` : '0';
                  }

                  sortedFormItems.push(child);
                });
              });
          });

          // Step 3: Prepare the rows and columns structure for the form
          const mappedFormItems: ColumnOrder[] = [];
          let rowIndex = 0;
          let previousRow = 0;

          sortedFormItems.forEach((item) => {
            const { row } = item.formSetting;

            if (row !== previousRow) {
              rowIndex += 1;
            }

            const colItems = sortedFormItems.filter((itm) => itm.formSetting.row === row).map((itm) => itm.formKey);

            mappedFormItems[rowIndex] = { cols: colItems };
            previousRow = row;
          });

          setRowsAndColsOrder(mappedFormItems);
          ConsoleHelper.log('mappedFormItems', mappedFormItems);
          ConsoleHelper.log('sortedFormItems', sortedFormItems);
          reset({ items: sortedFormItems });
          setFieldsReady(true);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setIsLoading(false);
      }
    };

    if (payingMember !== undefined && localAssId && showForm) {
      fetchFormSettings();
    }
  }, [localAssId, isMounted, payingMember, reset, showForm]);

  const saveFormData = React.useCallback(
    async (data: FormMemberResponse) => {
      setIsLoading(true);
      const formMemberPutItems: FormMemberPostItem[] = [];

      data.items.forEach((item) => {
        formMemberPutItems.push({
          formKey: item.formKey,
          formValue: Array.isArray(item.value) ? item.value.filter((x) => x !== null).join(',') : item.value,
        });
      });

      const formData: FormMemberPost = {
        skipValidation,
        items: formMemberPutItems,
        paid: payingMember,
      };
      ConsoleHelper.log('formData to post', formData);
      try {
        const res = await (otherContactMode ? createFormOther(formData) : createFormMember(formData));
        if (isMounted()) {
          toast.success(<>{t('member:editMember.saveSuccessMessage')}</>);
          setServerErrors([]);
          if (res.data) {
            // Visa dialog för att välja om man vill lägga till profiler på den nya medlemmen
            setCreatedMemberId(res.data as unknown as string);
            setShowProfileDialog(true);
          }
        }
      } catch (e: any) {
        if (isMounted()) {
          ConsoleHelper.log('saveFormDataError:', e);
          setServerErrors(e.data);
          scrollToModalTop();
        }
      } finally {
        if (isMounted()) setIsLoading(false);
      }
    },
    [skipValidation, payingMember, isMounted, t],
  );

  const onSubmit = React.useCallback(
    (data: FormMemberResponse) => {
      saveFormData(data);
    },
    [saveFormData],
  );

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const handleSkipValidation = () => {
    setSkipValidation(true);
  };

  // Posta på nytt om man som authority >= 4 satt skipValidation till true
  useEffect(() => {
    if (skipValidation) handleSubmit(onSubmit)();
  }, [skipValidation, handleSubmit, onSubmit]);

  useEffect(() => {
    ConsoleHelper.log('ass: ', currentUser?.associations);
    if (currentUser?.associations?.length === 1) {
      setLocalAssId(currentUser?.associations[0].localAss_id);
      setShowAssociations(false);
    } else {
      setShowAssociations(true);
    }
  }, [currentUser]);

  const getDialogTitle = () => {
    if (payingMember === undefined) return t('member:editMember.createNewMemberHeader', { label: displayLabel });
    if (payingMember) return t('member:editMember.createNewPayedMemberHeader', { label: displayLabel });
    if (!payingMember && payingMember !== undefined) t('member:editMember.createNewInterestedHeader');
    return t('member:editMember.createNewMemberHeader', { label: displayLabel });
  };

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={getDialogTitle()}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogActions={
        <Stack spacing={1} direction='row'>
          <Button size='large' variant='outlined' color='primary' onClick={onHandleClose}>
            {t('common:button.cancel')}
          </Button>
          {!showForm && (
            <Button
              size='large'
              disableElevation
              variant='contained'
              color='primary'
              onClick={() => setShowForm(true)}
              disabled={payingMember === undefined || !localAssId}
            >
              {t('common:button.continue')}
            </Button>
          )}
          {showForm && (
            <Button size='large' disableElevation variant='contained' color='primary' onClick={onHandleSave} disabled={loading}>
              {t('common:button.save')}
            </Button>
          )}
        </Stack>
      }
      dialogContent={
        <Box
          ref={modalTop}
          sx={{
            position: 'relative',
            border: (theme) => (serverErrors.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none'),
            minHeight: showForm ? '20rem' : null,
          }}
        >
          <MemberServerErrorSummary
            serverErrors={serverErrors}
            isMember={false}
            overridePermission={PermissionEnum.MemberCreateOverride}
            onClickOverride={() => handleSkipValidation()}
          />
          <LoadingOverlay loading={loading} />

          {!showForm && (
            <MemberCreateModeSelector
              payingMember={payingMember}
              payingMemberCallback={(x) => setPayingMember(x)}
              associationCallback={(x) => setLocalAssId(x)}
              showAssociation={showAssociations}
            />
          )}

          {payingMember !== undefined && localAssId && showForm && (
            <FormProvider {...formMethods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <MemberAccordionWrapper hasError={serverErrors.length > 0}>
                  <>
                    {fieldsReady &&
                      fields &&
                      rowsAndColsOrder &&
                      (() => {
                        const groupedFields = new Map<number, { groupName: string; items: FormItemResponse[] }>();

                        // Step 1: Group items by groupID
                        fields.forEach((field) => {
                          const { groupID } = field.formSetting;
                          const groupName = field.formSetting.groupName || 'Unnamed Group';

                          if (!groupedFields.has(groupID)) {
                            groupedFields.set(groupID, { groupName, items: [] });
                          }

                          groupedFields.get(groupID)?.items.push(field);
                        });

                        // Step 2: Render each group
                        return Array.from(groupedFields.entries()).map(([groupID, groupData], index) => {
                          const { groupName, items } = groupData;

                          // Group the items by row and column for rendering
                          const rows = items.reduce((acc, item) => {
                            const { row } = item.formSetting;
                            if (!acc[row]) acc[row] = [];
                            acc[row].push(item);
                            return acc;
                          }, {} as Record<number, FormItemResponse[]>);

                          return (
                            <Accordion key={groupID} disableGutters elevation={0} square defaultExpanded>
                              <AccordionSummary
                                expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
                                aria-controls={`panel-${index}-content`}
                                id={`panel-${index}-header`}
                              >
                                <Typography sx={{ fontWeight: 500 }}>{groupName}</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                {Object.entries(rows).map(([row, rowItems]) => (
                                  <Box sx={{ my: 1, mx: 1, mb: 3 }} key={row}>
                                    <GridWrapper>
                                      {rowItems.map((rowItem) => {
                                        const currentIndex = fields.findIndex(
                                          (y) =>
                                            y.formKey === rowItem.formKey &&
                                            y.formSetting.row === rowItem.formSetting.row &&
                                            y.formSetting.col === rowItem.formSetting.col,
                                        );
                                        return (
                                          <DynamicFormField
                                            key={`${rowItem.formKey}-${rowItem.formSetting.row}-${rowItem.formSetting.col}`}
                                            formItem={rowItem}
                                            index={currentIndex}
                                          />
                                        );
                                      })}
                                    </GridWrapper>
                                  </Box>
                                ))}
                              </AccordionDetails>
                            </Accordion>
                          );
                        });
                      })()}
                  </>
                </MemberAccordionWrapper>
              </form>
            </FormProvider>
          )}
          {createdMemberId && (
            <MemberAddProfileDialog
              open={showProfileDialog}
              onClose={() => {
                setShowProfileDialog(false);
                onSave();
                navigate(`${otherContactMode ? RoutesEnum.MemberOtherContactDetails : RoutesEnum.MemberDetails}/${createdMemberId}`);
              }}
              memberId={createdMemberId}
            />
          )}
        </Box>
      }
    />
  );
};
